/**
 * Typed client for the SDCP API, shared by both frontends.
 *
 * Response types come from `@sdcp/types`, generated from the API's OpenAPI
 * schema. A breaking API change fails the frontend typecheck in CI rather than
 * at runtime in a borrower's browser.
 *
 * Two rules this package exists to enforce:
 *
 *  - No secret ever reaches a browser bundle. Plaid keys, the Anthropic key, and
 *    signing keys stay server side.
 *  - Every request carries a correlation ID, so one borrower session can be
 *    traced across the frontend, the API, and an agent task in a single query.
 */

import type { components } from "@sdcp/types";

const BASE =
  (import.meta.env?.["VITE_API_BASE"] as string | undefined) ?? "/api";

export type Session = components["schemas"]["SessionOut"];
export type Requirement = components["schemas"]["RequirementOut"];
export type Turn = components["schemas"]["TurnOut"];
export type Choice = components["schemas"]["ChoiceOut"];

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly requestId: string | null,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const requestId = crypto.randomUUID();

  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "X-Request-ID": requestId,
      ...init?.headers,
    },
    credentials: "same-origin",
    referrerPolicy: "no-referrer",
  });

  if (!response.ok) {
    // Server error text is not surfaced verbatim — it can carry detail that does
    // not belong in a browser, and this client serves the borrower's app too.
    //
    // Two statuses are the exception, because they are the two a route author
    // writes a sentence *for the person on the screen*: a conflict and a
    // rejected value. Discarding those turned "Loan 123 already has a document
    // request" into "Request failed (409)" — an officer who has just typed a
    // number they used last week is told only that something went wrong, and
    // nothing on the form says which field to change.
    //
    // Only a string is taken: FastAPI's own 422 body is a list of validation
    // objects, which is machine detail and not a sentence.
    const detail =
      response.status === 409 || response.status === 422
        ? await response
            .json()
            .then((b: { detail?: unknown }) =>
              typeof b.detail === "string" ? b.detail : undefined,
            )
            .catch(() => undefined)
        : undefined;

    throw new ApiError(
      response.status,
      response.headers.get("X-Request-ID"),
      detail ??
        (response.status === 404
          ? "This link is not valid."
          : `Request failed (${response.status})`),
    );
  }

  return (await response.json()) as T;
}

/**
 * The borrower's session code.
 *
 * Held in sessionStorage rather than a URL so it stays out of Referer headers,
 * proxy logs, and browser history (ADR-0001). sessionStorage rather than
 * localStorage so it does not outlive the tab.
 *
 * The MVP has no OTP gate, so possession of this code is full access — which is
 * why the MVP carries no real borrower data (ADR-0006).
 */
const CODE_KEY = "sdcp.session";

export const sessionCode = {
  get: (): string | null => sessionStorage.getItem(CODE_KEY),
  set: (code: string) => sessionStorage.setItem(CODE_KEY, code),
  clear: () => sessionStorage.removeItem(CODE_KEY),
};

function authed(): HeadersInit {
  const code = sessionCode.get();
  if (!code) throw new ApiError(401, null, "No session.");
  return { "X-Session-Code": code };
}

export type Challenge = components["schemas"]["ChallengeOut"];
export type Entry = components["schemas"]["EntryOut"];

/**
 * Open a magic link.
 *
 * Returns either the session or the challenge guarding it, never both. The code
 * is stored either way — the gate endpoint needs it, and a challenge carries
 * nothing worth protecting.
 */
export async function openLink(code: string): Promise<Entry> {
  const entry = await request<Entry>(`/s/${encodeURIComponent(code)}`);
  sessionCode.set(code);
  return entry;
}

/**
 * Answer the gate.
 *
 * A wrong answer is a 401 whose message names the tries left; running out is a
 * 423. For a date of birth that is terminal — the officer issues a new link;
 * for an emailed code, a new code restores the tries. Every message is written
 * for the borrower, so each is surfaced verbatim.
 */
export const answerGate = (answer: string): Promise<Entry> =>
  gatePost("/gate", { answer });

/**
 * Email a one-time code to the address on the loan file.
 *
 * Returns the challenge again, now marked as sent. A 429 means wait — the
 * message says how long — and is surfaced verbatim like the rest.
 */
export const sendSignInCode = (): Promise<Entry> => gatePost("/gate/code");

async function gatePost(path: string, body?: unknown): Promise<Entry> {
  const code = sessionCode.get();
  if (!code) throw new ApiError(401, null, "No link.");

  const requestId = crypto.randomUUID();
  const response = await fetch(`${BASE}/s/${encodeURIComponent(code)}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Request-ID": requestId },
    body: body === undefined ? null : JSON.stringify(body),
    credentials: "same-origin",
    referrerPolicy: "no-referrer",
  });

  if (!response.ok) {
    const detail = await response
      .json()
      .then((b: { detail?: string }) => b.detail)
      .catch(() => undefined);
    throw new ApiError(
      response.status,
      response.headers.get("X-Request-ID"),
      detail ?? "That did not work.",
    );
  }
  return (await response.json()) as Entry;
}

export const getSession = () =>
  request<Session>("/v1/session", { headers: authed() });

/**
 * Something the borrower typed.
 *
 * The only path by which Verity hears their own words — every other call tells
 * it a sentence we wrote. Returns the whole session, like every borrower
 * action, so the client re-renders from server state rather than guessing.
 */
export const sendMessage = (text: string) =>
  request<Session>("/v1/session/message", {
    method: "POST",
    headers: authed(),
    body: JSON.stringify({ text }),
  });

/**
 * Set a document aside for later.
 *
 * The server moves it to the back of the queue rather than out of it, so it is
 * offered again once everything else is done. Returns the whole session, so the
 * client re-renders from server state rather than guessing what changed.
 */
export const skipRequirement = (requirementId: string) =>
  request<Session>(`/v1/session/requirements/${requirementId}/skip`, {
    method: "POST",
    headers: authed(),
  });

/**
 * Withdraw a document sent by mistake.
 *
 * The file is not deleted — it is marked replaced and leaves the lender's view.
 * If it was the only one on that requirement, the requirement reopens.
 */
export const replaceDocument = (documentId: string) =>
  request<Session>(`/v1/session/documents/${documentId}/replace`, {
    method: "POST",
    headers: authed(),
  });

/** Undo a skip. The document becomes the next thing asked for. */
export const resumeRequirement = (requirementId: string) =>
  request<Session>(`/v1/session/requirements/${requirementId}/resume`, {
    method: "POST",
    headers: authed(),
  });

/**
 * Manual upload — the fallback behind every automated path.
 *
 * A 422 carries text written for the borrower, so it is surfaced verbatim.
 * Every other status keeps the generic message.
 */
export async function uploadDocument(
  requirementId: string,
  file: File,
): Promise<Session> {
  const body = new FormData();
  body.append("file", file);

  const requestId = crypto.randomUUID();
  const response = await fetch(
    `${BASE}/v1/session/requirements/${requirementId}/upload`,
    {
      method: "POST",
      // No Content-Type header: the browser must set the multipart boundary.
      headers: { "X-Request-ID": requestId, ...authed() },
      body,
      credentials: "same-origin",
      referrerPolicy: "no-referrer",
    },
  );

  if (!response.ok) {
    let message = `Upload failed (${response.status})`;
    if (response.status === 422) {
      const detail = (await response.json().catch(() => null)) as {
        detail?: string;
      } | null;
      if (detail?.detail) message = detail.detail;
    }
    throw new ApiError(
      response.status,
      response.headers.get("X-Request-ID"),
      message,
    );
  }

  return (await response.json()) as Session;
}

// --- Plaid ----------------------------------------------------------------

export interface LinkToken {
  link_token: string;
}

/** Ask the server for a Link token. No institution is pre-selected — the
 *  borrower chooses their own bank inside Plaid. */
export const createLinkToken = (requirementId: string) =>
  request<LinkToken>(
    `/v1/session/requirements/${requirementId}/plaid/link-token`,
    {
      method: "POST",
      headers: authed(),
    },
  );

export interface ExchangePayload {
  public_token: string;
  institution_id?: string | undefined;
  institution_name?: string | undefined;
}

/** Hand back what Plaid Link returned. The public token is short-lived and
 *  useless on its own; the server exchanges it for the real credential, which
 *  never reaches the browser. */
export const exchangePublicToken = (
  requirementId: string,
  payload: ExchangePayload,
) =>
  request<Session>(`/v1/session/requirements/${requirementId}/plaid/exchange`, {
    method: "POST",
    headers: authed(),
    body: JSON.stringify(payload),
  });

export const startRequirement = (requirementId: string) =>
  request<Session>(`/v1/session/requirements/${requirementId}/start`, {
    method: "POST",
    headers: authed(),
  });

/**
 * A viewable link to a collected document.
 *
 * The session code travels as a query parameter here rather than a header,
 * because this URL is opened by the browser directly (a new tab, an <a href>)
 * where headers cannot be set. That is a deliberate exception to keeping the
 * code out of URLs — it is scoped to one document, opened by the borrower who
 * already holds the code, and the response is marked no-store.
 */
export function documentUrl(documentId: string): string {
  const code = sessionCode.get();
  return `${BASE}/v1/session/documents/${documentId}?code=${encodeURIComponent(code ?? "")}`;
}

export interface Health {
  status: string;
}

export const getHealth = () => request<Health>("/healthz");

/* ---------------------------------------------------------------------------
 * Loan officer
 *
 * A shared per-lender token, not a login. It establishes which lender is
 * calling and nothing else — no user, no actor, no audit trail. See
 * modules/lending/auth.py for what that does and does not buy.
 *
 * localStorage rather than sessionStorage, unlike the borrower's code: an
 * officer works across tabs and days, and the token is theirs rather than one
 * borrower's entry credential.
 * ------------------------------------------------------------------------- */

export type DocumentKind = components["schemas"]["DocumentKindOut"];
export type Product = components["schemas"]["ProductOut"];
export type StartingRow = components["schemas"]["StartingRowOut"];
export type RequestSummary = components["schemas"]["RequestSummaryOut"];
export type RequestDetail = components["schemas"]["RequestDetailOut"];
export type OfficerRequirement = components["schemas"]["OfficerRequirementOut"];
export type OfficerDocument = components["schemas"]["OfficerDocumentOut"];
export type CreateRequest = components["schemas"]["CreateRequestIn"];
export type CreatedRequest = components["schemas"]["CreateRequestOut"];
export type Routing = components["schemas"]["RoutingOut"];
export type Check = components["schemas"]["CheckOut"];
export type ExtractedField = components["schemas"]["ExtractedFieldOut"];
export type Trail = components["schemas"]["TrailOut"];
export type Draft = components["schemas"]["DraftOut"];
export type DeliverySettings = components["schemas"]["DeliverySettingsOut"];
export type DeliveryMethod = DeliverySettings["method"];
export type Submission = components["schemas"]["SubmissionOut"];
export type TrailStep = components["schemas"]["TrailStepOut"];

const OFFICER_KEY = "sdcp.officer";

export const officerToken = {
  get: (): string | null => localStorage.getItem(OFFICER_KEY),
  set: (token: string) => localStorage.setItem(OFFICER_KEY, token),
  clear: () => localStorage.removeItem(OFFICER_KEY),
};

function officerAuth(): HeadersInit {
  const token = officerToken.get();
  if (!token) throw new ApiError(401, null, "Not signed in.");
  return { "X-Officer-Token": token };
}

export const getCatalogue = () =>
  request<DocumentKind[]>("/v1/officer/catalogue", { headers: officerAuth() });

/**
 * How a document would be collected from a named institution.
 *
 * The officer's checklist promises to show whether an ask resolves itself or
 * lands on the borrower. Once naming a provider can change that, the answer has
 * to come from the server — the same call `create_request` makes — or the
 * checklist and the borrower's session disagree.
 */
export const getRouting = (kind: string, provider: string) =>
  request<Routing>(
    `/v1/officer/routing?kind=${encodeURIComponent(kind)}&provider=${encodeURIComponent(provider)}`,
    { headers: officerAuth() },
  );

export const listRequests = () =>
  request<RequestSummary[]>("/v1/officer/requests", { headers: officerAuth() });

export const getRequest = (id: string) =>
  request<RequestDetail>(`/v1/officer/requests/${id}`, {
    headers: officerAuth(),
  });

/**
 * Record that a person signed off on this document.
 *
 * Returns the whole loan file, because the submit gate depends on every
 * document's review state — a caller given only this document would have to
 * re-fetch to know whether the gate had opened, and would show a stale one in
 * the window between.
 */
export const markReviewed = (documentId: string) =>
  request<RequestDetail>(`/v1/officer/documents/${documentId}/reviewed`, {
    method: "POST",
    headers: officerAuth(),
  });

/**
 * Why this document, in the agent's own words.
 *
 * 404 for an uploaded document, which has no run and therefore no trail. That
 * is not an error — the provenance for an upload is that a person sent it — so
 * callers check `has_trail` rather than treating the 404 as a fault.
 */
/**
 * Confirm an extracted value, or replace it.
 *
 * Appends a decision beside the extractor's reading; it never edits it. Returns
 * the loan file so the panel can redraw from what it just changed.
 */
/**
 * Point a requirement at a different institution.
 *
 * Only while the borrower has not acted on it — the API answers 409 otherwise,
 * because a collected document means the question was already answered.
 */
export type Provider = components["schemas"]["ProviderOut"];

/**
 * Institutions we know, for the box an officer is typing into.
 *
 * Filtered by document kind, because an insurance row suggesting a bank is
 * noise — and noise in a suggestion list teaches people to ignore the list.
 */
export const searchProviders = (kind: string, q: string) =>
  request<Provider[]>(
    `/v1/officer/providers?kind=${encodeURIComponent(kind)}&q=${encodeURIComponent(q)}`,
    { headers: officerAuth() },
  );

/**
 * Correct what was typed from memory when the row was authored.
 *
 * Send only the field being changed. Absent means "leave it" — a caller that
 * sends both to change one is how the other gets quietly wiped.
 */
export const patchRequirement = (
  requirementId: string,
  patch: { provider?: string; subject_address?: string },
) =>
  request<RequestDetail>(`/v1/officer/requirements/${requirementId}`, {
    method: "PATCH",
    headers: officerAuth(),
    body: JSON.stringify(patch),
  });

export const reviewField = (
  documentId: string,
  field: string,
  body: { action: "confirmed" | "corrected"; value?: string },
) =>
  request<RequestDetail>(
    `/v1/officer/documents/${documentId}/fields/${encodeURIComponent(field)}`,
    { method: "POST", headers: officerAuth(), body: JSON.stringify(body) },
  );

export const getTrail = (documentId: string) =>
  request<Trail>(`/v1/officer/documents/${documentId}/trail`, {
    headers: officerAuth(),
  });

/**
 * Ask Verity to revise the document list while authoring a request.
 *
 * Stateless and writes nothing: the form is the single render of the list, so
 * it sends the list it holds and applies what comes back. A thread kept on the
 * server would be a second place the list lives.
 */
/**
 * Send the reminder the officer has just read.
 *
 * The blocker they were shown goes back with the request, because the server
 * renders the message from it — approving one message and sending another is
 * the failure this whole dialog exists to prevent.
 */
export const remind = (requestId: string, blocker: string) =>
  request<RequestSummary>(`/v1/officer/requests/${requestId}/remind`, {
    method: "POST",
    headers: officerAuth(),
    body: JSON.stringify({ blocker }),
  });

/**
 * Issue a new entry link for a borrower who never got theirs.
 *
 * Every link previously issued for the session stops working, which is why this
 * is a POST rather than a read of "the link" — there is no link to read, only a
 * hash of one.
 */
export const reissueLink = (requestId: string) =>
  request<{ url: string }>(`/v1/officer/requests/${requestId}/link`, {
    method: "POST",
    headers: officerAuth(),
  });

/** Where and how this lender's submitted files are delivered. */
export const getDeliverySettings = () =>
  request<DeliverySettings>("/v1/officer/settings/delivery", {
    headers: officerAuth(),
  });

/**
 * Set it. Server-side, unlike the rest of Settings today, because the server is
 * what sends to it — an address held in one officer's browser is one the server
 * has never heard of.
 */
export const putDeliverySettings = (settings: {
  email: string | null;
  method: DeliveryMethod;
}) =>
  request<DeliverySettings>("/v1/officer/settings/delivery", {
    method: "PUT",
    headers: officerAuth(),
    body: JSON.stringify(settings),
  });

/**
 * Hand the reviewed file to the lender.
 *
 * A 409 carries the sentence to show (not ready, no address, already sent). A
 * delivery that *fails* is not an error here: it comes back as a submission
 * with `state: "failed"`, so the officer sees why and can try again.
 */
export const submitRequest = (requestId: string) =>
  request<Submission>(`/v1/officer/requests/${requestId}/submit`, {
    method: "POST",
    headers: officerAuth(),
  });

/**
 * Download the submitted package and hand it to the browser as a save.
 *
 * Fetched, not linked — the endpoint needs the officer header, which an
 * `<a href>` cannot carry. The object URL is revoked once the click has been
 * dispatched, so the zip does not linger in memory for the life of the tab.
 */
export async function downloadPackage(requestId: string): Promise<void> {
  const response = await fetch(
    `${BASE}/v1/officer/requests/${requestId}/package`,
    {
      headers: officerAuth(),
      credentials: "same-origin",
      referrerPolicy: "no-referrer",
    },
  );
  if (!response.ok) {
    const detail = await response
      .json()
      .then((b: { detail?: unknown }) =>
        typeof b.detail === "string" ? b.detail : undefined,
      )
      .catch(() => undefined);
    throw new ApiError(
      response.status,
      response.headers.get("X-Request-ID"),
      detail ?? "Could not download the package.",
    );
  }
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename =
    /filename="([^"]+)"/.exec(disposition)?.[1] ?? "documents.zip";
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Products and their loan programs — what a request can be for (A2.1). */
export const getProducts = () =>
  request<Product[]>("/v1/officer/products", { headers: officerAuth() });

/** Answers about the loan, by facet: `{ income: ["w2", "rental"] }`. */
export type Facets = Record<string, string[]>;

/**
 * The list a program starts from, for what the officer has answered. A lookup
 * over the reference file, not a model call; it fills the unsaved form and
 * writes nothing.
 */
export const getStartingList = (
  product: string,
  program: string,
  facets: Facets,
) => {
  const q = new URLSearchParams(
    Object.entries(facets).flatMap(([facet, values]) =>
      values.map((v) => [facet, v]),
    ),
  ).toString();
  return request<StartingRow[]>(
    `/v1/officer/products/${product}/programs/${program}/documents${q ? `?${q}` : ""}`,
    { headers: officerAuth() },
  );
};

export const askVerity = (message: string, current: string[]) =>
  request<Draft>("/v1/officer/draft", {
    method: "POST",
    headers: officerAuth(),
    body: JSON.stringify({ message, current }),
  });

export const createRequest = (payload: CreateRequest) =>
  request<CreatedRequest>("/v1/officer/requests", {
    method: "POST",
    headers: officerAuth(),
    body: JSON.stringify(payload),
  });

/** Where a collected document can be opened. Authenticated, so fetched not linked. */
export async function openDocument(documentId: string): Promise<string> {
  const response = await fetch(`${BASE}/v1/officer/documents/${documentId}`, {
    headers: officerAuth(),
    credentials: "same-origin",
    referrerPolicy: "no-referrer",
  });
  if (!response.ok)
    throw new ApiError(response.status, null, "Could not open that document.");
  // An object URL rather than a direct link: the endpoint needs a header, which
  // an <a href> cannot carry. Caller revokes it.
  return URL.createObjectURL(await response.blob());
}
